import React, { useState } from 'react';
import { ChevronLeft, Camera, CheckCircle, User, AlertCircle, Shield } from 'lucide-react';

export default function FaceVerifyPage({ idCardData, onBack, onSubmit }) {
  const [faceVerified, setFaceVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleFaceVerify = () => {
    setIsVerifying(true);
    setTimeout(() => {
      setFaceVerified(true);
      setIsVerifying(false);
    }, 2000);
  };

  return (
    <div className="bg-slate-50 min-h-full pb-24">
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 px-4 py-3 border-b border-slate-200 flex items-center shadow-sm">
        <button onClick={onBack} className="p-1 -ml-2">
          <ChevronLeft size={24} className="text-slate-700" />
        </button>
        <span className="font-bold text-slate-800 ml-2 text-lg">人脸识别</span>
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-md">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={20} />
            <span className="font-bold">安全提示</span>
          </div>
          <p className="text-sm text-blue-100 leading-relaxed">
            为确保是您本人操作，需要进行人脸识别验证。请保持光线充足，正对摄像头，表情自然。
          </p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
            <h3 className="font-bold text-slate-800">人脸识别</h3>
          </div>

          <div className="flex flex-col items-center">
            <div
              className={`relative w-48 h-48 rounded-full border-4 flex flex-col items-center justify-center mb-4 transition-all ${
                faceVerified
                  ? 'border-green-400 bg-green-50'
                  : 'border-slate-300 bg-slate-50'
              }`}
            >
              {faceVerified ? (
                <>
                  <div className="absolute top-2 right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle size={20} className="text-white" />
                  </div>
                  <User size={64} className="text-green-500" />
                  <span className="text-sm font-bold text-green-600 mt-2">认证成功</span>
                </>
              ) : isVerifying ? (
                <>
                  <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-blue-600 mt-4">正在识别中...</span>
                </>
              ) : (
                <>
                  <User size={64} className="text-slate-400" />
                  <span className="text-sm text-slate-500 mt-2">点击下方按钮开始</span>
                </>
              )}
            </div>

            <button
              onClick={handleFaceVerify}
              disabled={faceVerified || isVerifying}
              className={`w-full py-3 rounded-xl font-bold text-white shadow-lg transition-all flex items-center justify-center gap-2 ${
                faceVerified
                  ? 'bg-green-500 cursor-default'
                  : isVerifying
                  ? 'bg-slate-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl active:scale-[0.98]'
              }`}
            >
              <Camera size={20} />
              {faceVerified ? '已认证' : isVerifying ? '识别中...' : '开始人脸识别'}
            </button>
          </div>
        </div>

        {idCardData && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
              <h3 className="font-bold text-slate-800">身份信息</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-sm text-slate-500">姓名</span>
                <span className="text-sm font-bold text-slate-800">{idCardData.name}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-sm text-slate-500">身份证号</span>
                <span className="text-sm font-bold text-slate-800">{idCardData.idNumber}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-500">有效期至</span>
                <span className="text-sm font-bold text-slate-800">{idCardData.validUntil}</span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-700 leading-relaxed">
              请确保光线充足，正对摄像头，表情自然。识别过程中请勿移动头部或遮挡面部。
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-16 left-0 right-0 z-20 flex justify-center pointer-events-none">
        <div className="w-full max-w-md bg-white border-t border-slate-200 p-4 pointer-events-auto">
          <button
            disabled={!faceVerified}
            onClick={onSubmit}
            className={`w-full py-3.5 rounded-xl font-bold text-white shadow-lg transition-all ${
              faceVerified
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl active:scale-[0.98]'
                : 'bg-slate-300 cursor-not-allowed'
            }`}
          >
            完成认证
          </button>
        </div>
      </div>
    </div>
  );
}
